'use client'

import {useEffect, useState} from 'react'
import {Bell, Clipboard, LayoutPanelLeft, Save, Sparkles} from 'lucide-react'
import {message} from '@/components/Toast'
import {PageHeader} from '@/components/PageHeader'
import {Switch} from '@/components/Switch'
import type {SettingsState} from '@/types'

export function SystemSettingsView({
    settings,
    saveSettings,
}: {
    settings: SettingsState
    saveSettings: (patch: Partial<SettingsState>) => Promise<void> | void
}) {
    const system = settings.system
    const [systemDraft, setSystemDraft] = useState(system)
    const [permission, setPermission] = useState<NotificationPermission | 'unsupported' | 'insecure'>('default')

    useEffect(() => {
        setSystemDraft(system)
    }, [system])

    function refreshPermission() {
        if (!window.isSecureContext) {
            setPermission('insecure')
        } else if (!('Notification' in window)) {
            setPermission('unsupported')
        } else {
            setPermission(Notification.permission)
        }
    }

    useEffect(() => {
        refreshPermission()
        document.addEventListener('visibilitychange', refreshPermission)
        return () => document.removeEventListener('visibilitychange', refreshPermission)
    }, [])

    async function requestNotificationPermission(): Promise<boolean> {
        if (!window.isSecureContext) {
            setPermission('insecure')
            message.error('当前页面不是安全上下文，无法申请通知权限')
            return false
        }
        if (!('Notification' in window)) {
            setPermission('unsupported')
            message.error('当前浏览器不支持系统通知')
            return false
        }
        if (Notification.permission === 'denied') {
            setPermission('denied')
            message.warning('当前站点通知已被 Chrome 拒绝，请在地址栏左侧的网站设置中改为允许')
            return false
        }
        const nextPermission = Notification.permission === 'granted'
            ? 'granted'
            : await Notification.requestPermission()
        setPermission(nextPermission)
        if (nextPermission !== 'granted') {
            message.warning('Chrome 未授予通知权限，请检查地址栏左侧的通知拦截图标')
            return false
        }
        return true
    }

    async function toggleMessageNotifications() {
        const enabled = !system.messageNotifications
        if (enabled && !(await requestNotificationPermission())) return
        await saveSettings({
            system: {
                ...system,
                messageNotifications: enabled,
            },
        })
    }

    async function updateConversationLayout(layout: 'single' | 'multi') {
        await saveSettings({
            system: {
                ...system,
                conversationLayout: layout,
            },
        })
    }

    async function copyChromeSettingsAddress() {
        await navigator.clipboard.writeText('chrome://settings/content/notifications')
        message.success('已复制 Chrome 通知设置地址，请粘贴到地址栏打开')
    }

    async function saveAIReplySettings() {
        const historyLimit = Math.min(200, Math.max(5, systemDraft.aiReplyHistoryLimit || 50))
        await saveSettings({
            system: {
                ...systemDraft,
                aiReplyHistoryLimit: historyLimit,
                aiReplyPrompt: (systemDraft.aiReplyPrompt || '').trim(),
            },
        })
    }

    const permissionText = permission === 'granted'
        ? '当前站点已获得通知权限'
        : permission === 'denied'
            ? '当前站点通知已被 Chrome 拒绝'
            : permission === 'default'
                ? '当前站点尚未获得通知权限'
                : permission === 'insecure'
                    ? '当前页面不是安全上下文，无法申请通知权限'
                    : '当前浏览器不支持系统通知'

    return (
        <section className="flex h-full flex-col bg-page">
            <PageHeader title="系统设置" description="配置桌面提醒与消息通知"/>
            <div className="flex-1 overflow-y-auto p-6">
                <div className="mr-auto w-full max-w-4xl space-y-8">
                    <section>
                        <div className="mb-3 flex items-center gap-2">
                            <LayoutPanelLeft size={18} className="text-accent"/>
                            <h3 className="text-sm font-medium text-ink">常用设置</h3>
                        </div>
                        <div className="divide-y divide-hair rounded-lg border border-hair bg-white">
                            <div className="flex items-center justify-between gap-6 p-5">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink">对话框布局</div>
                                    <div className="mt-1 text-sm leading-relaxed text-muted">
                                        单实例只显示当前实例的会话；多实例会按当前在线实例分组展示会话列表。
                                    </div>
                                </div>
                                <div className="flex shrink-0 rounded-md border border-hair bg-page p-0.5">
                                    {([
                                        ['single', '单实例'],
                                        ['multi', '多实例'],
                                    ] as const).map(([value, label]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => void updateConversationLayout(value)}
                                            className={`h-8 rounded px-4 text-sm font-medium transition-colors ${
                                                (system.conversationLayout || 'single') === value
                                                    ? 'bg-white text-accent shadow-sm'
                                                    : 'text-soft hover:text-ink'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="min-w-0 pr-10">
                                        <div className="mb-1 flex items-center gap-2 text-sm font-medium text-ink">
                                            <Bell size={16} className="text-muted"/>
                                            消息系统通知
                                        </div>
                                        <div className="text-sm leading-relaxed text-muted">
                                            开启后，收到新的微信消息时将通过操作系统通知提醒。
                                        </div>
                                    </div>
                                    <Switch
                                        checked={system.messageNotifications}
                                        onChange={() => void toggleMessageNotifications()}
                                    />
                                </div>
                                <div className="mt-5 rounded border border-hair bg-gray-50 p-4">
                                    <div className="text-sm text-ink">{permissionText}</div>
                                    <div className="mt-2 text-xs leading-relaxed text-muted">
                                        如果 Chrome 没有弹出授权框，请查看地址栏左侧的网站设置或通知拦截图标，将通知改为“允许”。
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => void requestNotificationPermission()}
                                        >
                                            <Bell size={15}/>
                                            申请通知权限
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => void copyChromeSettingsAddress()}
                                        >
                                            <Clipboard size={15}/>
                                            复制 Chrome 通知设置地址
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className="mb-3 flex items-center gap-2">
                            <Sparkles size={18} className="text-accent"/>
                            <h3 className="text-sm font-medium text-ink">AI 生成回复</h3>
                        </div>
                        <div className="space-y-5 rounded-lg border border-hair bg-white p-5">
                            <div className="space-y-2">
                                <label className="text-sm text-soft">参考聊天记录条数</label>
                                <input
                                    type="number"
                                    min={5}
                                    max={200}
                                    className="input"
                                    value={systemDraft.aiReplyHistoryLimit ?? 50}
                                    onChange={(event) => {
                                        const value = Number(event.target.value)
                                        setSystemDraft({
                                            ...systemDraft,
                                            aiReplyHistoryLimit: Number.isFinite(value) ? value : 50,
                                        })
                                    }}
                                    onBlur={() => {
                                        const value = Math.min(200, Math.max(5, systemDraft.aiReplyHistoryLimit || 50))
                                        setSystemDraft({...systemDraft, aiReplyHistoryLimit: value})
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm text-soft">提示词</label>
                                <textarea
                                    rows={6}
                                    value={systemDraft.aiReplyPrompt || ''}
                                    onChange={(event) => setSystemDraft({...systemDraft, aiReplyPrompt: event.target.value})}
                                    className="w-full rounded border border-hair bg-white px-3 py-2 text-sm leading-relaxed text-ink outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                                />
                            </div>
                            <div className="flex justify-end border-t border-hair pt-4">
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => void saveAIReplySettings()}
                                >
                                    <Save size={16}/>
                                    保存 AI 生成回复设置
                                </button>
                            </div>
                        </div>
                    </section>

                    <footer className="pt-2 pb-4 text-center text-xs text-muted">
                        系统版本号 v{__APP_VERSION__}
                    </footer>
                </div>
            </div>
        </section>
    )
}
